// Iter-196.W (C5 from audit) — Winner-aware sticky pill rendered in
// two surfaces:
//   1. `StickyCompareDrawer` minimized rail (when stash has ≥2 items
//      AND drawer is collapsed) — replaces the plain "Compare (N)"
//      with a winner-led "Best: {name}" pill so visitors see the
//      editorial recommendation even without opening the drawer.
//   2. `CompareDrawerModal` header — prominent "Editor's Pick"
//      banner above the spec table, calling out the highest-rated
//      lender + the WHY (top rating, lowest APR, etc.).
//
// Winner derivation rules:
//   • Sort by `rating` desc (highest editor score wins).
//   • Tie-breakers by category:
//       - auto-loan / refinance → lower `min_apr` wins
//       - insurance / warranty → lower `details.avg_monthly_premium`
//                                or `details.deductible_default` wins
//   • If `rating` is null on all items, fall back to lowest `min_apr`.
//
// No LLM, no API call — pure derivation from the existing stash items
// that the CompareStashProvider already hydrates. Zero added latency.
import { Trophy, Star } from "lucide-react";

/**
 * Pick the editorial winner from a compare stash.
 * @param {Array} items - stash items from useCompareStash().items
 * @returns {{ winner: Object, reason: string } | null}
 */
export function pickCompareWinner(items) {
  if (!Array.isArray(items) || items.length < 2) return null;
  const cat = items[0]?.category;
  // Iter-459 — No overall "winner" for insurance: CarSavr has no defensible
  // cross-carrier methodology (quotes depend on driver, ZIP and coverage).
  if (cat === "insurance") return null;
  const ratingOf = (it) => (typeof it.rating === "number" ? it.rating : 0);
  const aprOf = (it) =>
    typeof it.min_apr === "number" ? it.min_apr : Number.POSITIVE_INFINITY;
  const premiumOf = (it) => {
    const v = it.details?.avg_monthly_premium;
    return typeof v === "number" ? v : Number.POSITIVE_INFINITY;
  };
  const deductibleOf = (it) => {
    const v = it.details?.deductible_default;
    return typeof v === "number" ? v : Number.POSITIVE_INFINITY;
  };

  const sorted = [...items].sort((a, b) => {
    // Primary: rating desc
    const ra = ratingOf(a);
    const rb = ratingOf(b);
    if (rb !== ra) return rb - ra;
    // Tie-break per category
    if (cat === "auto-loan" || cat === "refinance") {
      return aprOf(a) - aprOf(b);
    }
    if (cat === "insurance") {
      return premiumOf(a) - premiumOf(b);
    }
    if (cat === "warranty") {
      return deductibleOf(a) - deductibleOf(b);
    }
    return 0;
  });

  const winner = sorted[0];
  // Build a short single-sentence reason so visitors understand WHY
  // this lender topped the list (not just a black-box "best").
  let reason = "highest editor rating";
  if (winner.rating == null) {
    if (cat === "auto-loan" || cat === "refinance") reason = "lowest APR in stash";
    else if (cat === "insurance") reason = "lowest premium in stash";
    else if (cat === "warranty") reason = "lowest deductible in stash";
    else reason = "best overall match";
  } else {
    reason = `${Number(winner.rating).toFixed(1)}/5 editor rating`;
  }
  return { winner, reason };
}

/**
 * Inline winner pill for the compare drawer's minimized rail.
 * Compact horizontal layout: trophy icon + "Best: {name}" + reason.
 */
export function CompareWinnerRailPill({ items }) {
  const result = pickCompareWinner(items);
  if (!result) return null;
  const { winner, reason } = result;
  return (
    <span
      data-testid="compare-winner-rail-pill"
      className="inline-flex items-center gap-1.5 bg-[#10B981]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#10B981] leading-none"
      title={`Editor's pick — ${reason}`}
    >
      <Trophy className="w-3 h-3" strokeWidth={2.5} />
      <span data-testid="compare-winner-rail-name">{winner.name}</span>
    </span>
  );
}

/**
 * Full winner banner for the CompareDrawerModal header. Stacks the
 * trophy + name + reason + a star rating callout. Sits between the
 * affiliate disclosure and the state picker.
 */
export function CompareWinnerBanner({ items, onApply }) {
  const result = pickCompareWinner(items);
  if (!result) return null;
  const { winner, reason } = result;
  return (
    <div
      data-testid="compare-winner-banner"
      className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#0A2540] text-white px-4 py-3"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-9 h-9 bg-[#10B981] grid place-items-center shrink-0">
          <Trophy className="w-4 h-4 text-[#0A2540]" strokeWidth={2.25} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#10B981] leading-none mb-1">
            Editor&apos;s pick
          </p>
          <p
            data-testid="compare-winner-banner-name"
            className="font-display font-black text-lg leading-tight truncate"
          >
            {winner.name}
          </p>
          <p
            data-testid="compare-winner-banner-reason"
            className="text-[11px] text-slate-300 mt-0.5 inline-flex items-center gap-1.5"
          >
            {winner.rating != null && (
              <Star className="w-3 h-3 text-[#10B981] fill-[#10B981]" />
            )}
            {reason}
          </p>
        </div>
      </div>
      {onApply && (
        <button
          type="button"
          onClick={() => onApply(winner)}
          data-testid="compare-winner-banner-cta"
          className="inline-flex items-center justify-center bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold h-10 px-4 text-sm transition-colors shrink-0"
        >
          Apply with {winner.name}
        </button>
      )}
    </div>
  );
}
